import React, { useState, useEffect } from 'react';

const KnowledgeBaseManager = () => {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filter, setFilter] = useState({ category: '', search: '' });
  const [stats, setStats] = useState(null);

  const [formData, setFormData] = useState({
    category: '',
    title: '',
    content: '',
    keywords: ''
  });

  // State untuk tag-based keywords
  const [keywordTags, setKeywordTags] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'https://chatpanel.my.id/api';

  useEffect(() => {
    loadEntries();
    loadCategories();
    loadStats();
  }, [filter]);

  const loadEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.search) params.append('search', filter.search);

      const response = await fetch(`${API_URL}/ai-chat/knowledge-base?${params}`);
      const data = await response.json();
      setEntries(Array.isArray(data[0]) ? data[0] : data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading entries:', error);
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/ai-chat/knowledge-base/categories/list`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/ai-chat/stats`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Handle keyword tag input (Enter key)
  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      e.preventDefault();
      const newKeyword = keywordInput.trim();
      
      // Avoid duplicates
      if (!keywordTags.includes(newKeyword)) {
        const newTags = [...keywordTags, newKeyword];
        setKeywordTags(newTags);
        setFormData({...formData, keywords: newTags.join(', ')});
      }
      
      setKeywordInput('');
    }
  };

  // Remove keyword tag
  const removeKeywordTag = (tagToRemove) => {
    const newTags = keywordTags.filter(tag => tag !== tagToRemove);
    setKeywordTags(newTags);
    setFormData({...formData, keywords: newTags.join(', ')});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingEntry 
        ? `${API_URL}/ai-chat/knowledge-base/${editingEntry.id}`
        : `${API_URL}/ai-chat/knowledge-base`;
      
      const method = editingEntry ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ category: '', title: '', content: '', keywords: '' });
        setKeywordTags([]);
        setEditingEntry(null);
        loadEntries();
        loadCategories();
        alert(editingEntry ? 'Entry updated successfully!' : 'Entry created successfully!');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Error saving entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      content: entry.content,
      keywords: entry.keywords || ''
    });
    
    // Parse keywords into tags
    const tags = entry.keywords ? entry.keywords.split(',').map(k => k.trim()).filter(k => k) : [];
    setKeywordTags(tags);
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        const response = await fetch(`${API_URL}/ai-chat/knowledge-base/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          loadEntries();
          alert('Entry deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting entry:', error);
        alert('Error deleting entry');
      }
    }
  };

  const openAddModal = () => {
    setEditingEntry(null);
    setFormData({ category: '', title: '', content: '', keywords: '' });
    setKeywordTags([]);
    setShowModal(true);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>📚 Knowledge Base Manager</h2>
        <button onClick={openAddModal} style={styles.addButton}>
          ➕ Add New Entry
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>📊</div>
            <div style={styles.statValue}>{entries.length}</div>
            <div style={styles.statLabel}>KB Entries</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>🏷️</div>
            <div style={styles.statValue}>{categories.length}</div>
            <div style={styles.statLabel}>Categories</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>💬</div>
            <div style={styles.statValue}>{stats.total_chats || 0}</div>
            <div style={styles.statLabel}>Total Queries</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.filtersCard}>
        <div style={styles.filters}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>🏷️ Category</label>
            <select 
              value={filter.category} 
              onChange={(e) => setFilter({...filter, category: e.target.value})}
              style={styles.select}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>🔍 Search</label>
            <input
              type="text"
              placeholder="Search by title or content..."
              value={filter.search}
              onChange={(e) => setFilter({...filter, search: e.target.value})}
              style={styles.searchInput}
            />
          </div>
        </div>
      </div>

      {/* Entries List */}
      {loading ? (
        <div style={styles.loadingState}>
          <div style={styles.spinner}></div>
          <p>Loading entries...</p>
        </div>
      ) : (
        <div style={styles.entriesList}>
          {entries.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📝</div>
              <h3 style={styles.emptyTitle}>No Entries Found</h3>
              <p style={styles.emptyText}>Add your first knowledge base entry to get started!</p>
              <button onClick={openAddModal} style={styles.emptyButton}>
                ➕ Create First Entry
              </button>
            </div>
          ) : (
            entries.map(entry => (
              <div key={entry.id} style={styles.entryCard}>
                <div style={styles.entryHeader}>
                  <span style={styles.categoryBadge}>{entry.category}</span>
                  <div style={styles.entryActions}>
                    <button 
                      onClick={() => handleEdit(entry)} 
                      style={styles.editBtn}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(entry.id)} 
                      style={styles.deleteBtn}
                      onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                      onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
                
                <h3 style={styles.entryTitle}>{entry.title}</h3>
                
                <p style={styles.entryContent}>
                  {entry.content.substring(0, 150)}
                  {entry.content.length > 150 ? '...' : ''}
                </p>
                
                {entry.keywords && (
                  <div style={styles.keywordsContainer}>
                    <span style={styles.keywordsLabel}>🏷️ Keywords:</span>
                    <div style={styles.keywordsWrapper}>
                      {entry.keywords.split(',').slice(0, 3).map((keyword, idx) => (
                        <span key={idx} style={styles.keywordTag}>
                          {keyword.trim()}
                        </span>
                      ))}
                      {entry.keywords.split(',').length > 3 && (
                        <span style={styles.moreKeywords}>
                          +{entry.keywords.split(',').length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                <div style={styles.entryFooter}>
                  <span style={styles.entryDate}>
                    🕒 Updated: {new Date(entry.updated_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {editingEntry ? '✏️ Edit Entry' : '➕ New Entry'}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                style={styles.closeButton}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  🏷️ Category <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., FAQ, Product, Support"
                  required
                  list="categories-list"
                />
                <datalist id="categories-list">
                  {categories.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  📝 Title <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  style={styles.input}
                  placeholder="Enter a clear, descriptive title"
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  📄 Content <span style={styles.required}>*</span>
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  style={styles.textarea}
                  placeholder="Enter detailed content that will be used to answer questions..."
                  rows="8"
                  required
                />
              </div>

              {/* Tag-based Keyword Input */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  🏷️ Keywords
                </label>
                <div 
                  style={styles.tagInputWrapper}
                  onClick={() => document.querySelector('#keyword-input').focus()}
                >
                  {/* Keyword Tags */}
                  {keywordTags.map((tag, index) => (
                    <div key={index} style={styles.tagChip}>
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeKeywordTag(tag);
                        }}
                        style={styles.tagRemoveBtn}
                        onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.5)'}
                        onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  
                  {/* Input Field */}
                  <input
                    id="keyword-input"
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    style={styles.tagInput}
                    placeholder={keywordTags.length === 0 ? "Type keyword and press Enter..." : "Add more..."}
                  />
                </div>
                <div style={styles.helperText}>
                  💡 Press <strong>Enter</strong> to add keyword • Click <strong>✕</strong> to remove
                </div>
              </div>

              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  style={styles.cancelBtn}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(107, 114, 128, 0.1)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.7)'}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  style={styles.saveBtn}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 20px rgba(220, 38, 38, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.2)';
                  }}
                >
                  {editingEntry ? '💾 Update Entry' : '✅ Create Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    animation: 'fadeInUp 0.6s ease'
  },
  
  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0
  },
  addButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    color: 'white',
    border: 'none',
    padding: '14px 28px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
    transition: 'all 0.3s ease'
  },

  // Stats Cards
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px',
    marginBottom: '32px'
  },
  statCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    textAlign: 'center',
    transition: 'all 0.3s ease'
  },
  statIcon: {
    fontSize: '32px',
    marginBottom: '12px'
  },
  statValue: {
    fontSize: '36px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px'
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '500'
  },

  // Filters
  filtersCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '32px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)'
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '20px'
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  select: {
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid rgba(220, 38, 38, 0.1)',
    fontSize: '14px',
    background: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  },
  searchInput: {
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid rgba(220, 38, 38, 0.1)',
    fontSize: '14px',
    background: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease'
  },

  // Entries List
  entriesList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '24px'
  },
  entryCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    transition: 'all 0.3s ease',
    animation: 'fadeInUp 0.6s ease'
  },
  entryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    gap: '12px'
  },
  categoryBadge: {
    background: 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
    color: 'white',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },
  entryActions: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0
  },
  editBtn: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    padding: '8px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
  },
  deleteBtn: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    border: 'none',
    padding: '8px 14px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.2)'
  },
  entryTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: '12px',
    lineHeight: '1.4'
  },
  entryContent: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.7',
    marginBottom: '16px'
  },
  keywordsContainer: {
    marginBottom: '16px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(0, 0, 0, 0.05)'
  },
  keywordsLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '8px',
    display: 'block'
  },
  keywordsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '6px'
  },
  keywordTag: {
    display: 'inline-block',
    padding: '4px 10px',
    background: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  moreKeywords: {
    padding: '4px 10px',
    background: 'rgba(107, 114, 128, 0.1)',
    color: '#6b7280',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  entryFooter: {
    paddingTop: '12px',
    borderTop: '1px solid rgba(0, 0, 0, 0.05)'
  },
  entryDate: {
    fontSize: '12px',
    color: '#999',
    fontWeight: '500'
  },

  // Empty State
  emptyState: {
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '80px 20px',
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    border: '2px dashed rgba(220, 38, 38, 0.2)'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333',
    marginBottom: '8px'
  },
  emptyText: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '24px'
  },
  emptyButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    color: 'white',
    border: 'none',
    padding: '14px 28px',
    borderRadius: '12px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)'
  },

  // Loading State
  loadingState: {
    textAlign: 'center',
    padding: '80px',
    color: '#666'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid rgba(220, 38, 38, 0.1)',
    borderTopColor: '#dc2626',
    borderRadius: '50%',
    margin: '0 auto 16px',
    animation: 'spin 1s linear infinite'
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    animation: 'slideIn 0.3s ease'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '28px 32px 20px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)'
  },
  modalTitle: {
    fontSize: '28px',
    fontWeight: '700',
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    margin: 0
  },
  closeButton: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    color: '#dc2626',
    fontSize: '24px',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease'
  },

  // Form
  form: {
    padding: '24px 32px 32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  required: {
    color: '#dc2626'
  },
  input: {
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid rgba(220, 38, 38, 0.1)',
    fontSize: '14px',
    background: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.3s ease'
  },
  textarea: {
    padding: '12px 16px',
    borderRadius: '10px',
    border: '2px solid rgba(220, 38, 38, 0.1)',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    background: 'rgba(255, 255, 255, 0.9)',
    lineHeight: '1.6',
    transition: 'all 0.3s ease'
  },

  // Tag Input
  tagInputWrapper: {
    minHeight: '100px',
    padding: '12px',
    border: '2px solid rgba(220, 38, 38, 0.1)',
    borderRadius: '10px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    alignItems: 'flex-start',
    background: 'rgba(255, 255, 255, 0.9)',
    cursor: 'text',
    transition: 'all 0.3s ease'
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    animation: 'slideIn 0.2s ease',
    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
  },
  tagRemoveBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    background: 'rgba(255, 255, 255, 0.3)',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    color: 'white',
    fontSize: '14px',
    lineHeight: '1',
    padding: 0,
    transition: 'all 0.2s ease'
  },
  tagInput: {
    flex: 1,
    minWidth: '200px',
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    background: 'transparent',
    padding: '8px 4px'
  },
  helperText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px'
  },

  // Modal Actions
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    paddingTop: '8px'
  },
  cancelBtn: {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(107, 114, 128, 0.2)',
    color: '#6b7280',
    padding: '12px 28px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    color: 'white',
    border: 'none',
    padding: '12px 32px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
    transition: 'all 0.3s ease'
  }
};

export default KnowledgeBaseManager;
